import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PracticeMode = ({ verse, mode, onComplete, onHint, className = '' }) => {
  const [userInput, setUserInput] = useState('');
  const [revealedWords, setRevealedWords] = useState(new Set());
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [shuffledWords, setShuffledWords] = useState([]);
  const [selectedWords, setSelectedWords] = useState([]);
  const [showHint, setShowHint] = useState(false);

  const words = verse?.text?.split(' ');

  useEffect(() => {
    if (mode === 'word-order') {
      const shuffled = [...words]?.sort(() => Math.random() - 0.5);
      setShuffledWords(shuffled);
    }
  }, [verse, mode]);

  const handleFillInBlank = () => {
    const blankedText = words?.map((word, index) => {
      if (index % 3 === 0) return '_____';
      return word;
    })?.join(' ');

    return (
      <div className="space-y-6">
        <div className="bg-muted/50 rounded-lg p-4 sm:p-6">
          <p className="text-base sm:text-lg leading-relaxed text-foreground font-medium">
            {blankedText}
          </p>
        </div>
        <div className="space-y-4">
          <textarea
            value={userInput}
            onChange={(e) => setUserInput(e?.target?.value)}
            placeholder="Type the complete verse here..."
            className="w-full h-36 sm:h-32 p-4 border border-border rounded-lg bg-input text-foreground resize-none
              focus:ring-2 focus:ring-ring focus:border-transparent focus:outline-none
              transition-all duration-200 hover:border-border/80 text-base sm:text-sm"
            style={{ fontSize: '16px' }}
          />
          
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowHint(!showHint);
                onHint?.();
              }}
              iconName="Lightbulb"
              iconPosition="left"
              className="transition-all duration-200 hover:scale-105 active:scale-95 focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-warning/15 hover:text-warning hover:shadow-md min-h-[48px] px-4"
            >
              {showHint ? 'Hide Hint' : 'Show Hint'}
            </Button>
            
            <Button
              variant="default"
              onClick={() => onComplete?.(userInput)}
              iconName="Check"
              iconPosition="left"
              className="transition-all duration-200 hover:scale-105 active:scale-95 focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:shadow-lg bg-gradient-to-r from-primary to-primary/80 min-h-[48px] px-5"
            >
              Check Answer
            </Button>
          </div>
          
          {showHint && (
            <div className="bg-gradient-to-r from-warning/15 via-warning/8 to-transparent border border-warning/30 rounded-lg p-4 animate-fadeIn shadow-sm">
              <p className="text-sm text-accent-foreground">
                <Icon name="Info" size={16} className="inline mr-2" />
                Focus on the missing words: {words?.filter((_, i) => i % 3 === 0)?.join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleWordOrder = () => {
    const handleWordSelect = (word, index) => {
      if (selectedWords?.includes(word)) return;
      setSelectedWords([...selectedWords, word]);
      setShuffledWords(shuffledWords?.filter((_, i) => i !== index));
    };

    const handleWordRemove = (wordIndex) => {
      const word = selectedWords?.[wordIndex];
      setSelectedWords(selectedWords?.filter((_, i) => i !== wordIndex));
      setShuffledWords([...shuffledWords, word]);
    };

    return (
      <div className="space-y-6">
        <div className="bg-muted/50 rounded-lg p-4 sm:p-6 min-h-24">
          <p className="text-base sm:text-lg leading-relaxed text-foreground font-medium">
            {selectedWords?.length > 0 ? selectedWords?.join(' ') : 'Select words in the correct order...'}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
          {shuffledWords?.map((word, index) => (
            <Button
              key={`${word}-${index}`}
              variant="outline"
              onClick={() => handleWordSelect(word, index)}
              className="min-h-[48px] text-sm transition-all duration-150 hover:scale-105 hover:shadow-warm active:scale-95
                focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-primary/10 hover:border-primary/50 hover:text-primary"
            >
              {word}
            </Button>
          ))}
        </div>
        {selectedWords?.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Selected words:</p>
            <div className="flex flex-wrap gap-2">
              {selectedWords?.map((word, index) => (
                <Button
                  key={`selected-${index}`}
                  variant="secondary"
                  size="sm"
                  onClick={() => handleWordRemove(index)}
                  iconName="X"
                  iconPosition="right"
                  iconSize={14}
                  className="transition-all duration-150 hover:scale-105 active:scale-95 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[40px]"
                >
                  {word}
                </Button>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedWords([]);
              setShuffledWords([...words]?.sort(() => Math.random() - 0.5));
            }}
            iconName="RotateCcw"
            iconPosition="left"
            className="transition-all duration-200 hover:scale-105 active:scale-95 focus:ring-2 focus:ring-ring focus:ring-offset-2 min-h-[48px] px-4"
          >
            Reset
          </Button>
          
          <Button
            variant="default"
            onClick={() => onComplete?.(selectedWords?.join(' '))}
            disabled={selectedWords?.length !== words?.length}
            iconName="Check"
            iconPosition="left"
            className="transition-all duration-200 hover:scale-105 active:scale-95 focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:hover:scale-100 min-h-[48px] px-5"
          >
            Check Order
          </Button>
        </div>
      </div>
    );
  };

  const handleProgressiveReveal = () => {
    const handleRevealNext = () => {
      if (currentWordIndex < words?.length) {
        setRevealedWords(new Set([...revealedWords, currentWordIndex]));
        setCurrentWordIndex(currentWordIndex + 1);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-muted/50 rounded-lg p-6">
          <p className="text-lg leading-relaxed text-foreground font-medium">
            {words?.map((word, index) => (
              <span
                key={index}
                className={`transition-all duration-300 ${
                  revealedWords?.has(index) 
                    ? 'text-foreground' 
                    : 'text-transparent bg-muted rounded px-1'
                } mr-2`}
              >
                {revealedWords?.has(index) ? word : '•'?.repeat(word?.length)}
              </span>
            ))}
          </p>
        </div>
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-4">
            <span className="text-sm text-muted-foreground">
              Progress: {revealedWords?.size}/{words?.length} words
            </span>
            <div className="w-32 bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(revealedWords?.size / words?.length) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-3">
            <Button
              variant="outline"
              onClick={handleRevealNext}
              disabled={currentWordIndex >= words?.length}
              iconName="Eye"
              iconPosition="left"
              className="transition-all duration-200 hover:scale-105 active:scale-95 focus:ring-2 focus:ring-ring focus:ring-offset-2
                hover:bg-primary/10 hover:border-primary/50 hover:text-primary disabled:hover:scale-100"
            >
              Reveal Next Word
            </Button>
            
            <Button
              variant="default"
              onClick={() => onComplete?.(words?.join(' '))}
              disabled={revealedWords?.size < words?.length}
              iconName="Check"
              iconPosition="left"
              className="transition-all duration-200 hover:scale-105 active:scale-95 focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:hover:scale-100"
            >
              Complete
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderMode = () => {
    switch (mode) {
      case 'fill-blank':
        return handleFillInBlank();
      case 'word-order':
        return handleWordOrder();
      case 'progressive':
        return handleProgressiveReveal();
      default:
        return handleFillInBlank();
    }
  };

  return (
    <div className={`bg-card rounded-lg p-6 shadow-warm ${className}`}>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-heading font-semibold text-card-foreground">
            {verse?.reference}
          </h3>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Brain" size={16} />
            <span className="capitalize">{mode?.replace('-', ' ')} Mode</span>
          </div>
        </div>
      </div>
      {renderMode()}
    </div>
  );
};

export default PracticeMode;